import { Bookmark, ChevronDown, Loader2, Plus, RotateCw, Users, X } from 'lucide-react';
import { useState, useEffect } from 'react';
import '../styles/team.css';
import { SearchSelect } from './FieldComponents';
import { formatLastModified } from './NestedFeed';
import { getContactInitialsClassName } from '../utils/colorUtils';

const normalizeRoleAssignments = (roleValue) => {
    if (!roleValue || typeof roleValue !== 'object') {
        return [null, []];
    }

    const firstValue = Array.isArray(roleValue)
        ? roleValue[0]
        : roleValue[0] ?? roleValue['0'] ?? null;
    const otherValues = Array.isArray(roleValue)
        ? roleValue[1]
        : roleValue[1] ?? roleValue['1'] ?? [];

    const numericFirstUserId = Number(firstValue);
    const firstUserId = Number.isFinite(numericFirstUserId) && numericFirstUserId > 0
        ? numericFirstUserId
        : null;
    const normalizedOtherValues = Array.isArray(otherValues)
        ? otherValues
            .map(value => Number(value))
            .filter(value => Number.isFinite(value) && value > 0 && value !== firstUserId)
        : [];

    return [firstUserId, Array.from(new Set(normalizedOtherValues))];
};

const normalizeTeamData = (teamData = {}) => {
    const normalized = {};

    Object.entries(teamData || {}).forEach(([roleId, roleValue]) => {
        const [firstUserId, otherUserIds] = normalizeRoleAssignments(roleValue);
        if (firstUserId === null && otherUserIds.length === 0) {
            return;
        }

        normalized[roleId] = [firstUserId, otherUserIds];
    });

    return normalized;
};

const formatHistoryDateTime = (dateString) => {
    if (!dateString) return '';
    return formatLastModified(dateString)?.[5] || '';
};

export const Team = ({ case_id, onTeamUpdated }) => {
    const [roles, setRoles] = useState([]);
    const [teamData, setTeamData] = useState({});
    const [users, setUsers] = useState([]);
    const [activeTeamPage, setActiveTeamPage] = useState(0);
    const [screenWidth, setScreenWidth] = useState(window.innerWidth);
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState('');
    const [openRole, setOpenRole] = useState(null);
    const [editRoleMembers, setEditRoleMembers] = useState(false);
    const [addingMember, setAddingMember] = useState(false);
    const [teamHistory, setTeamHistory] = useState([]);
    const [historyLoading, setHistoryLoading] = useState(false);
    const [historyError, setHistoryError] = useState('');

    const notifyTeamUpdated = () => {
        if (typeof onTeamUpdated === 'function') {
            onTeamUpdated();
        }
    };

    const fetchRoles = async () => {
        const response = await fetch(`https://api.casedb.co/team.php`, {
            credentials: 'include'
        });
        const data = await response.json();

        if (data.success) {
            setRoles(data.roles);
        }
    }
    
    const fetchUsers = async () => {
        try {
            const response = await fetch(`https://api.casedb.co/user.php`, {
                credentials: 'include'
            });
            const data = await response.json();
            
            if (data.success) {
                setUsers(data.users);
            }
        } catch (error) {
            console.error('Error fetching users:', error);
        }
    }
    
    const fetchCaseTeam = async () => {
        if (!case_id) return;
        
        try {
            const response = await fetch(`https://api.casedb.co/get_case_team.php?case_id=${case_id}`, {
                credentials: 'include'
            });
            const data = await response.json();
            
            if (data.success) {
                setTeamData(normalizeTeamData(data.team || {}));
            }
        } catch (error) {
            console.error('Error fetching case team:', error);
        }
    }

    const fetchTeamHistory = async () => {
        if (!case_id) return;

        setHistoryLoading(true);
        setHistoryError('');

        try {
            const response = await fetch(`https://api.casedb.co/team.php?case_id=${case_id}&history=true`, {
                credentials: 'include'
            });
            const data = await response.json();

            if (data.success) {
                setTeamHistory(Array.isArray(data.team_history) ? data.team_history : []);
            } else {
                setHistoryError(data.message || 'Failed to load team history');
            }
        } catch (error) {
            console.error('Error fetching team history:', error);
            setHistoryError('Failed to load team history');
        } finally {
            setHistoryLoading(false);
        }
    }
    
    const saveTeamData = async () => {
        if (!case_id) {
            setMessage('No case selected');
            return;
        }
        
        setLoading(true);
        try {
            const response = await fetch('https://api.casedb.co/team.php', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                credentials: 'include',
                body: JSON.stringify({
                    case_id: case_id,
                    team_data: normalizeTeamData(teamData)
                })
            });
            
            const data = await response.json();
            if (data.success) {
                notifyTeamUpdated();
                if (activeTeamPage === 3) {
                    fetchTeamHistory();
                }
                setMessage('Team saved successfully');
                setTimeout(() => setMessage(''), 3000);
            } else {
                setMessage(`Error: ${data.message}`);
            }
        } catch (error) {
            console.error('Error saving team data:', error);
            setMessage('Error saving team data');
        } finally {
            setLoading(false);
        }
    }
    
    const handleRoleAssignment = (roleKey, position, userId) => {
        setTeamData(prevData => {
            const newData = normalizeTeamData(prevData);
            const [currentFirst, currentOthers] = normalizeRoleAssignments(newData[roleKey]);
            const userIdInt = userId ? parseInt(userId) : null;
    
            if (position === 0) {
                const nextOthers = currentOthers.filter(id => id !== userIdInt);

                if (userIdInt === null && nextOthers.length === 0) {
                    delete newData[roleKey];
                } else {
                    newData[roleKey] = [userIdInt, Array.from(new Set(nextOthers))];
                }
            } else {
                if (userIdInt !== null && !currentOthers.includes(userIdInt)) {
                    const nextOthers = Array.from(new Set([...currentOthers, userIdInt]));
                    newData[roleKey] = [currentFirst, nextOthers];

                    // Trigger backend update when user is added
                    fetch('https://api.casedb.co/team.php', {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                        },
                        credentials: 'include',
                        body: JSON.stringify({
                            case_id: case_id,
                            team_data: normalizeTeamData(newData),
                            change_type: 'add_member',
                            source: 'team_ui',
                            assignment_position: position,
                            target_user_id: userIdInt
                        })
                    })
                    .then(res => res.json())
                    .then(data => {
                        if (data.success) {
                            notifyTeamUpdated();
                            if (activeTeamPage === 3) {
                                fetchTeamHistory();
                            }
                        } else {
                            console.error('Error syncing new team member:', data.message);
                        }
                    })
                    .catch(err => console.error('Fetch error while syncing member:', err));
                }
            }
    
            if (newData[roleKey] && newData[roleKey][0] === null && newData[roleKey][1].length === 0) {
                delete newData[roleKey];
            }
    
            return newData;
        });
    };

    const handleFirstMemberClick = (roleId, userId) => {
        updateFirstMember(roleId, userId);
        setOpenRole(roles.findIndex(r => r.id === roleId));
    };

    const updateFirstMember = (roleId, newFirstId) => {
        setTeamData(prev => {
            const newFirstIdInt = parseInt(newFirstId);
            const normalizedPrev = normalizeTeamData(prev);
            const role = normalizedPrev[roleId] || [null, []];
            const [, currentOthers] = normalizeRoleAssignments(role);
    
            const others = new Set(
                currentOthers
                    .map(id => parseInt(id))
                    .filter(id => id && !isNaN(id) && id !== newFirstIdInt)
            );

            const updatedTeamData = {
                ...normalizedPrev,
                [roleId]: [newFirstIdInt, Array.from(others)],
            };
    
            // Persist
            fetch('https://api.casedb.co/team.php', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({
                    case_id,
                    role_id: roleId,
                    first: newFirstIdInt,
                    new_first: true,
                    change_type: 'set_first',
                    source: 'team_ui',
                    assignment_position: 0,
                    target_user_id: newFirstIdInt,
                    team_data: normalizeTeamData(updatedTeamData),
                })
            }).then(res => res.json()).then(data => {
                if (data.success) {
                    notifyTeamUpdated();
                    if (activeTeamPage === 3) {
                        fetchTeamHistory();
                    }
                } else {
                    console.error('Backend update failed:', data.message);
                }
            }).catch(err => console.error('Error updating team first member:', err));
    
            return updatedTeamData;
        });
    };
    
    const removeUserFromRole = (roleKey, position, userId) => {
        setTeamData(prevData => {
            const newData = normalizeTeamData(prevData);
            const role = newData[roleKey] || [null, []];
            const [currentFirst, currentOthers] = normalizeRoleAssignments(role);

            if (position === 0) {
                if (currentFirst === null && currentOthers.length === 0) {
                    return newData;
                }

                if (currentOthers.length > 0) {
                    newData[roleKey] = [currentOthers[0], currentOthers.slice(1)];
                } else {
                    delete newData[roleKey];
                }

                return newData;
            }

            const userIdInt = parseInt(userId);
            const nextOthers = currentOthers.filter(id => id !== userIdInt);
            if (currentFirst === null && nextOthers.length === 0) {
                delete newData[roleKey];
            } else {
                newData[roleKey] = [currentFirst, nextOthers];
            }
            
            return newData;
        });
    }

    const handleRemoveTeamMember = async (roleId, userId) => {
        const userIdInt = parseInt(userId);
        const role = teamData[roleId];
        
        if (!role) return;
        
        // Determine if user is primary (position 0) or additional (position 1)
        const isPrimary = Number(role["0"]) === userIdInt;
        const position = isPrimary ? 0 : 1;
        
        // Update local state
        removeUserFromRole(roleId, position, userIdInt);
        
        // Update backend
        try {
            const updatedRole = { ...role };
            const [currentFirst, currentOthers] = normalizeRoleAssignments(role);
            let nextRole = [currentFirst, [...currentOthers]];
            
            if (isPrimary) {
                if (nextRole[1].length > 0) {
                    nextRole = [nextRole[1][0], nextRole[1].slice(1)];
                } else {
                    nextRole = [null, []];
                }
            } else {
                nextRole = [currentFirst, currentOthers.filter(id => id !== userIdInt)];
            }
            
            const updatedTeamData = {
                ...normalizeTeamData(teamData),
                [roleId]: nextRole[0] !== null || nextRole[1].length > 0 ? nextRole : null
            };
            
            if (!updatedTeamData[roleId]) {
                delete updatedTeamData[roleId];
            }
            
            const response = await fetch('https://api.casedb.co/team.php', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                credentials: 'include',
                body: JSON.stringify({
                    case_id: case_id,
                    team_data: normalizeTeamData(updatedTeamData),
                    change_type: 'remove_member',
                    source: 'team_ui',
                    assignment_position: position,
                    target_user_id: userIdInt,
                    removed_user_id: userIdInt,
                    role_id: roleId
                })
            });
            
            const data = await response.json();
            if (!data.success) {
                console.error('Error removing team member:', data.message);
                // Revert local state on error
                fetchCaseTeam();
            } else {
                notifyTeamUpdated();
                if (activeTeamPage === 3) {
                    fetchTeamHistory();
                }
            }
        } catch (error) {
            console.error('Error removing team member:', error);
            // Revert local state on error
            fetchCaseTeam();
        }
    };

    const getUserName = (userId) => {
        const user = users.find(u => Number(u.id) === parseInt(userId));
        return user ? user.name : 'Unknown';
    }

    useEffect(() => {
        fetchRoles();
        fetchUsers();
        fetchCaseTeam();
        
        const handleResize = () => setScreenWidth(window.innerWidth);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, [case_id]);

    useEffect(() => {
        if (case_id && activeTeamPage === 3) {
            fetchTeamHistory();
        }
    }, [case_id, activeTeamPage]);

    useEffect(() => {
        if (openRole === null || roles.length === 0) return;

        const activeRole = roles[openRole];
        if (!activeRole) return;

        const [firstUserId, otherUserIds] = normalizeRoleAssignments(teamData?.[activeRole.id]);
        if (firstUserId === null && otherUserIds.length === 0) {
            setOpenRole(null);
        }
    }, [openRole, roles, teamData]);

    const getTeamUserById = (id) => users.find(user => Number(user.id) === Number(id));

    const buildHistoryInitialsSeed = (fallbackName = '', fallbackHandle = '') => {
        const source = `${fallbackName || ''}${fallbackHandle || ''}`.trim();
        if (!source) return 1;

        return Array.from(source).reduce((sum, char) => sum + char.charCodeAt(0), 0) || 1;
    };

    const renderHistoryInitials = (userId, fallbackName = '', fallbackHandle = '') => {
        const user = getTeamUserById(userId);
        if (user) {
            return renderTeamMemberInitials(user.id);
        }

        const displayName = fallbackName || fallbackHandle || 'Unknown';
        const initials = displayName
            .trim()
            .split(/\s+/)
            .filter(Boolean)
            .slice(0, 2)
            .map(part => part.charAt(0).toUpperCase())
            .join('') || 'U';
        const initialsClassName = getContactInitialsClassName(buildHistoryInitialsSeed(fallbackName, fallbackHandle));

        return <span className={initialsClassName}>{initials}</span>;
    };

    const renderHistoryAction = (row) => {
        const actionType = String(row.action || row.mode || '').toLowerCase();
        const label = row.action_label || (actionType === 'remove_member' ? 'Removed' : actionType === 'promote' ? 'Promoted' : actionType === 'set_first' ? 'Primary' : 'Added');
        const className = actionType === 'remove_member'
            ? 'removed'
            : actionType === 'promote'
                ? 'promoted'
                : actionType === 'set_first'
                    ? 'primary'
                : 'added';

        return <span className={`team-history-action-badge ${className}`}>{label}</span>;
    };

    const renderTeamMemberInitials = (id) => {
        const user = getTeamUserById(id);

        if (!user) return null;

        const initialsClassName = getContactInitialsClassName(user.id);
    
        if (user?.profile_picture) {
            return <img src={`https://api.casedb.co/${user.profile_picture}`} alt={user.name} className={initialsClassName} />;
        }

        if (user?.name) {
            const initials = user.name
                .trim()
                .split(/\s+/)
                .filter(Boolean)
                .slice(0, 2)
                .map(part => part.charAt(0).toUpperCase())
                .join('');

            return <span className={initialsClassName}>{initials}</span>;
        }
    
        return null;
    };

    const renderUserHandle = (userHandle) => {
        if (!userHandle) return null;
        return `@${String(userHandle).toLowerCase()}`;
    };

        const renderHistoryPerson = (userId, fallbackName = '', fallbackHandle = '') => {
            const user = getTeamUserById(userId);
            const displayName = user?.name || fallbackName || 'Unknown';
            const displayHandle = user?.user || fallbackHandle || '';

            return (
                <div className='team-history-person'>
                    {renderHistoryInitials(userId, displayName, displayHandle)}
                    <div className='team-history-person-text'>
                        <span>{displayName}</span>
                        {renderUserHandle(displayHandle) ? <span className='small-text'>{renderUserHandle(displayHandle)}</span> : null}
                    </div>
                </div>
            );
        };

        const renderHistoryTab = () => (
            <div className='team-history-container'>
                <div className='team-history-wrapper'>
                    <div className='user-actions team-history-actions'>
                        <span className='subtext'>
                            {historyLoading ? 'Loading team history...' : `${teamHistory.length} history item${teamHistory.length === 1 ? '' : 's'}`}
                        </span>
                        <div className='reload small-text' onClick={fetchTeamHistory}>
                            {historyLoading ? <Loader2 size={14} className='spin' /> : <RotateCw size={14} />}
                            Refresh History
                        </div>
                    </div>

                    {historyError ? <div className='error'>{historyError}</div> : null}

                    {historyLoading ? (
                        <div className='team-history-empty subtext'>Loading team history...</div>
                    ) : teamHistory.length > 0 ? (
                        <table className='team-history-table'>
                            <thead className='small-text'>
                                <tr>
                                    <th>Action</th>
                                    <th>Role</th>
                                    <th>First?</th>
                                    <th>Assignee</th>
                                    <th>Assigner</th>
                                    <th>Date/Time</th>
                                </tr>
                            </thead>
                            <tbody className='small-text'>
                                {teamHistory.map(row => (
                                    <tr key={row.history_key || `${row.log_id}-${row.role_id}-${row.assignee_id}`}>
                                        <td className='team-history-action-cell'>
                                            {renderHistoryAction(row)}
                                        </td>
                                        <td className='team-history-role-cell'>{row.role_name || `Role ${row.role_id || ''}`}</td>
                                        <td className='team-history-flag-cell'>{row.is_first ? 'Yes' : 'No'}</td>
                                        <td className='team-history-person-cell'>
                                            {renderHistoryPerson(row.assignee_id, row.assignee_name, row.assignee_user)}
                                        </td>
                                        <td className='team-history-person-cell'>
                                            {renderHistoryPerson(row.assigner_id, row.assigner_name, row.assigner_user)}
                                        </td>
                                        <td className='team-history-datetime-cell'>
                                            {formatHistoryDateTime(row.created_at) || '-'}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    ) : (
                        <div className='team-history-empty subtext'>No team history recorded for this case.</div>
                    )}
                </div>
            </div>
        );
    
    const renderRolesTab = () => (
        <div className='roles-container'>
            <table className='roles-table'>
                <thead>
                    <tr>
                        <th className='roles-role'>Role</th>
                        <th className='roles-members'>Members</th>
                        <th className='roles-first'>First</th>
                    </tr>
                </thead>
                <tbody>
                    {roles.map((role, index) => (
                        <tr key={index} className={`roles-row ${openRole === index ? 'open' : ''}`} onClick={() => {setEditRoleMembers(prev => editRoleMembers === role ? null : role); setOpenRole(null);}}>
                            <td className='roles-role'>{role.role}</td>
                            <td className='roles-members'>
                                {[teamData?.[role?.id]?.[0], ...(teamData?.[role?.id]?.[1] || [])]?.filter(userId => users?.some(u => Number(u?.id) === Number(userId)))?.length}
                            </td>
                            {openRole != index ? 
                                <td className='roles-first' onClick={(e) => {
                                    e.stopPropagation();
                                    setOpenRole(openRole === index ? null : teamData?.[role.id]?.[0] != null ? index : null);
                                }}>
                                    <div className='roles-first-info'>
                                            {renderTeamMemberInitials(teamData?.[role.id]?.[0])}
                                        {users.find(user => Number(user.id) === teamData?.[role.id]?.[0])?.name ?? ""}
                                    </div>
                                    {users.find(user => Number(user.id) === teamData?.[role.id]?.[0])?.name && <ChevronDown size={18} />}
                                </td>
                            : 
                                <td className='open-role' onClick={(e) => {
                                    e.stopPropagation();
                                    setOpenRole(openRole === index ? null : index);
                                }}>
                                    {(() => {
                                        const [openFirstUserId, openOtherUserIds] = normalizeRoleAssignments(teamData?.[role.id]);
                                        const openFirstUser = getTeamUserById(openFirstUserId);
                                        const openFirstHandle = renderUserHandle(openFirstUser?.user);

                                        return (
                                    <div className='open-role-selection'>
                                        <div className='open-role-member'>
                                            {renderTeamMemberInitials(openFirstUserId)}
                                            <div className='open-role-user'>
                                                <div className='role-member-user-info'>
                                                    {openFirstUser?.name ?? ""}
                                                    {openFirstHandle ? <span className='small-text'>{openFirstHandle}</span> : null}
                                                </div>
                                                <div className='open-role-set-first'>
                                                    <Bookmark
                                                        color={openFirstUserId ? "var(--fill)" : "transparent"}
                                                        stroke={openFirstUserId ? "var(--fill)" : "var(--subtext-color)"}
                                                        size={16}
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleFirstMemberClick(role.id, openFirstUserId);
                                                            setOpenRole(null);
                                                        }}
                                                        style={{ cursor: 'pointer' }}
                                                        title="Set as primary"
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                        {openOtherUserIds.filter(userId => users.some(u => Number(u.id) === Number(userId))).map((userId, userIndex) => (
                                            <div key={userIndex} className='open-role-member' onClick={() => {updateFirstMember(role.id, userId); setOpenRole(editRoleMembers?.id);}}>
                                                {renderTeamMemberInitials(userId)}
                                                <div className='open-role-user'>
                                                    <div className='role-member-user-info'>
                                                        {users.find(user => Number(user.id) === userId)?.name ?? ""}
                                                        {renderUserHandle(users.find(user => Number(user.id) === userId)?.user) ? <span className='small-text'>{renderUserHandle(users.find(user => Number(user.id) === userId)?.user)}</span> : null}
                                                    </div>
                                                    <div className='open-role-set-first'>
                                                        <Bookmark
                                                            color={teamData?.[role.id]?.[0] === userId ? "var(--fill)" : "transparent"}
                                                            stroke={teamData?.[role.id]?.[0] === userId ? "var(--fill)" : "var(--subtext-color)"}
                                                            size={16}
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                handleFirstMemberClick(role.id, userId);
                                                                setOpenRole(null);
                                                            }}
                                                            style={{ cursor: 'pointer' }}
                                                            title="Set as primary"
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                        );
                                    })()}
                                </td>
                            }
                        </tr>
                    ))}
                </tbody>
            </table>
            {editRoleMembers && (
                <div className='role-members-sidebar'>
                    <h3 className='role-sidebar-title'>{editRoleMembers.role}<button className='action-tiny tertiary' onClick={() => setAddingMember(true)}><Plus size={18} /> Add Member</button></h3>
                    <div className='horizontal divider' />
                    {addingMember && (
                        <SearchSelect
                            options={Object.fromEntries(users.map(user => [user.id, user.name]))}
                            value={null}
                            onChange={(val) => {
                                handleRoleAssignment(editRoleMembers.id, 1, val);
                                setAddingMember(false); // Close the dropdown after adding
                            }}
                            placeholder='Add Member'
                        />   
                    )}
                    <div className='role-members-list'>
                        {users
                            ?.filter(user => {
                                const role = teamData?.[editRoleMembers.id];
                                const first = Number(role?.[0]);
                                const additional = role?.[1] || [];
                                return first === Number(user.id) || additional.includes(Number(user.id));
                            })
                            .map((user, index) => (
                                <div key={index} className='open-role-member'>
                                    <div className='open-role-member-info'>
                                        {renderTeamMemberInitials(Number(user.id))}
                                        <div className='role-member-user'>
                                            <div className='role-member-user-info'>
                                                <span className='role-member-name'>
                                                    {user.name}{' '}
                                                </span>
                                                <span className='small-text role-member-username'>
                                                    {user.user ? `@${String(user.user).toLowerCase()}` : ''}
                                                </span>
                                            </div>
                                            <div className='role-member-actions'>
                                                <div className='open-role-set-first'>
                                                    <Bookmark
                                                        color={
                                                            Number(teamData?.[editRoleMembers.id]?.[0]) === Number(user.id)
                                                                ? "var(--fill)"
                                                                : "transparent"
                                                        }
                                                        stroke={
                                                            Number(teamData?.[editRoleMembers.id]?.[0]) === Number(user.id)
                                                                ? "var(--fill)"
                                                                : "var(--subtext-color)"
                                                        }
                                                        size={16}
                                                        className='role-member-bookmark'
                                                        onClick={() => handleFirstMemberClick(editRoleMembers.id, user.id)}
                                                        style={{ cursor: 'pointer' }}
                                                        title="Set as primary"
                                                    />
                                                </div>
                                                <div className='open-role-remove'>
                                                    <X 
                                                        onClick={() => handleRemoveTeamMember(editRoleMembers.id, user.id)}
                                                        title="Remove from role"
                                                        size={20}
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))
                        }
                    </div>
                </div>
            )}
        </div>
    );

    return (
        <div className='case-section'>
            <div id='template-header' className='alt-header'>
                {screenWidth < 729 ? (
                    <select className='default-select' value={activeTeamPage} onChange={(e) => setActiveTeamPage(Number(e.target.value))}>
                        <option value={0}>Roles</option>
                        {/*<option value={1}>Teams</option>
                        <option value={2}>Members</option>*/}
                        <option value={3}>History</option>
                    </select>
                )  : (
                    <>
                        <h4 onClick={() => setActiveTeamPage(0)} className={`${activeTeamPage !== 0 ? 'in' : ''}active`}>Roles</h4>
                        {/*<h4 onClick={() => setActiveTeamPage(1)} className={`${activeTeamPage !== 1 ? 'in' : ''}active`}>Teams</h4>
                        <h4 onClick={() => setActiveTeamPage(2)} className={`${activeTeamPage !== 2 ? 'in' : ''}active`}>Members</h4>*/}
                        <h4 onClick={() => setActiveTeamPage(3)} className={`${activeTeamPage !== 3 ? 'in' : ''}active`}>History</h4>
                    </>
                )}
            </div>
            
            {activeTeamPage === 0 && renderRolesTab()}
            {/*activeTeamPage === 1 && <div>Teams tab content</div>}
            {activeTeamPage === 2 && <div>Members tab content</div>*/}
            {activeTeamPage === 3 && renderHistoryTab()}
        </div>
    )
}
